//#region imports
import React, { useMemo, useState, useEffect, useContext } from 'react';

// constants
import { EmployeeJobCardWidth, JobCard30MinLayoutWidth, JobCardWidth, minute30MinLayoutWidth } from '../../timeline-constants';

// utils
import { getTimeString, calculateMinuteDifference, deriveFlightIdFromTask } from "_utils/functions";

// shared context from EmployeeList
import { JobListSharedContext } from "../../employee-components/employee-list";

//#endregion

const JobCardContainer = React.memo((props) => {

    // shared values from context (single subscription in EmployeeList)
    const shared = useContext(JobListSharedContext);
    const flightSlotLookup = shared?.flightSlotLookup;
    const iata = shared?.iata;

    // extract props
    const { job, thirtyMinLayout, disabled, showFullCard } = props;

    const maxHeight = '60px';
    const minHeight = '22.5px';

    // states
    const [nowDate, setNowDate] = useState(() => new Date());
    const now = getTimeString(nowDate);
    const [height, setHeight] = useState(minHeight);    // default to min height
    const [hover, setHover] = useState(false);

    // refresh current time every minute while an active task is shown so the width stays accurate
    useEffect(() => {
        if (!(job?.active && !job?.completed)) return;
        const tick = () => setNowDate(new Date());
        tick();
        const id = setInterval(tick, 60000);
        return () => clearInterval(id);
    }, [job?.active, job?.completed]);

    // calculate job card width (starts from assignedTime, grows to current time/completeTime)
    const jobCardMetrics = useMemo(() => {
        // check if thirty min layout is enabled
        if (!thirtyMinLayout) {
            return { width: EmployeeJobCardWidth, offset: 0, debug: { branch: 'non-30min-layout', width: EmployeeJobCardWidth } };
        }

        // derive start time: prefer task start time, else live flight slot, else assignedTime
        // returns { timeString, date } — date is null when only an HH:MM string is available
        const flightId = deriveFlightIdFromTask(job, iata);

        const { timeString: startTimeString, date: startDate, source: startSource } = (() => {
            if (job?.startTime) {
                const { seconds, nanoseconds, _seconds, _nanoseconds } = job.startTime;

                if (typeof seconds === "number" || typeof _seconds === "number") {
                    const ms = (seconds ?? _seconds) * 1000 + ((nanoseconds ?? _nanoseconds ?? 0) / 1_000_000);
                    const d = new Date(ms);
                    return { timeString: getTimeString(d), date: d, source: 'job.startTime (firestore)' };
                }

                const d = new Date(job.startTime);
                return { timeString: getTimeString(d), date: d, source: 'job.startTime (raw)' };
            }

            if (flightId && flightSlotLookup?.[flightId]) {
                return { timeString: flightSlotLookup[flightId], date: null, source: 'flightSlotLookup' };
            }

            return { timeString: job?.assignedTime, date: null, source: 'job.assignedTime' };
        })();

        // guard: if we couldn't derive a valid start time, fall back to a safe default width
        if (!startTimeString) {
            return { width: JobCard30MinLayoutWidth, offset: 0, debug: { branch: 'no-start-time', startSource, startTimeString, startDate, width: JobCard30MinLayoutWidth } };
        }

        // check if job is active (but not completed)
        if (job?.active && !job?.completed) {

            let widthInMinutes;
            let calcMethod;
            let endTimeUsed;

            if (startDate) {
                // date-based calculation — handles midnight crossing correctly
                const endDate = nowDate;
                calcMethod = 'date-based';
                endTimeUsed = endDate.toISOString();
                const diffMs = endDate.getTime() - startDate.getTime();
                widthInMinutes = Math.max(0, diffMs / 60000);
            } else {
                // fallback: only HH:MM strings available (flightSlotLookup / assignedTime)
                let currentTime = now;
                calcMethod = 'string-based';
                endTimeUsed = currentTime;

                // string comparison for "ahead of current time" guard
                if (startTimeString > currentTime) {
                    return { width: JobCard30MinLayoutWidth, offset: 0, debug: { branch: 'active-ahead-of-time', calcMethod, startSource, startTimeString, startDate, endTimeUsed, width: JobCard30MinLayoutWidth } };
                }

                widthInMinutes = calculateMinuteDifference(startTimeString, currentTime);
            }

            // cap at 12 hours to prevent runaway widths from bad time data
            let width = Math.min(Math.max(widthInMinutes * minute30MinLayoutWidth, 10), 720 * minute30MinLayoutWidth);

            width += 10; // to account for borders/padding

            return { width, offset: 5, debug: { branch: 'active', calcMethod, startSource, startTimeString, startDate: startDate?.toISOString(), endTimeUsed, widthInMinutes, pxPerMin: minute30MinLayoutWidth, widthPx: width } };
        }

        // check if job is completed
        if (job?.completed) {
            const finish = new Date(job.completeTime.seconds * 1000);

            let widthInMinutes;
            let calcMethod;

            if (startDate) {
                // date-based: handles midnight crossing correctly
                const diffMs = finish.getTime() - startDate.getTime();
                widthInMinutes = Math.max(0, diffMs / 60000);
                calcMethod = 'date-based';
            } else {
                // fallback: only HH:MM strings available
                const finishTimeString = getTimeString(finish);
                // if finish is before start on the clock, the job completed before its
                // scheduled slot (e.g. flight delay pushed assignedTime past actual finish)
                // — don't wrap midnight, just show minimum width
                if (finishTimeString < startTimeString) {
                    return { width: JobCard30MinLayoutWidth, offset: 0, debug: { branch: 'completed-before-slot', calcMethod: 'string-based', startSource, startTimeString, endTime: finish.toISOString(), width: JobCard30MinLayoutWidth } };
                }
                widthInMinutes = calculateMinuteDifference(startTimeString, finishTimeString);
                calcMethod = 'string-based';
            }

            const width = Math.min(Math.max(widthInMinutes * minute30MinLayoutWidth, 10), 720 * minute30MinLayoutWidth);

            return { width, offset: 0, debug: { branch: 'completed', calcMethod, startSource, startTimeString, startDate: startDate?.toISOString(), endTime: finish.toISOString(), widthInMinutes, pxPerMin: minute30MinLayoutWidth, widthPx: width } };
        }

        // default width for unstarted jobs
        const width = job?.assignedTime && !job?.completed && !job?.active
            ? EmployeeJobCardWidth
            : JobCard30MinLayoutWidth;

        return { width, offset: 0, debug: { branch: 'unstarted-default', startSource, startTimeString, assignedTime: job?.assignedTime, active: job?.active, completed: job?.completed, widthPx: width } };

    }, [job, job?.completed, job?.active, job?.completeTime, job?.assignedTime, thirtyMinLayout, iata, now, nowDate]);

    // extract width and offset
    const { width: jobCardWidth, offset: jobCardOffset, debug: jobCardDebug } = jobCardMetrics;

    const handleLog = () => { };

    // 5-minute interval width for minimum card size
    const intervalWidth = minute30MinLayoutWidth * 5;

    // Show full height when card is too narrow to display content properly
    const isNarrowCard = thirtyMinLayout && disabled && jobCardWidth < EmployeeJobCardWidth;
    const effectiveHeight = isNarrowCard ? maxHeight : (props?.showFullCard ? maxHeight : thirtyMinLayout ? height : maxHeight);

    return (
        <div
            onMouseOver={() => { setHeight(maxHeight); setHover(true); }}
            onMouseOut={() => { setHeight(minHeight); setHover(false); }}
            onClick={handleLog}
            style={{
                //borderStyle:'solid', borderWidth:1,
                position: 'relative',
                overflowY: 'hidden',
                //overflowX: 'visible',
                width: thirtyMinLayout && disabled ? jobCardWidth : EmployeeJobCardWidth + 10,
                //minWidth:20,
                minWidth: thirtyMinLayout ? (hover ? EmployeeJobCardWidth : intervalWidth) : EmployeeJobCardWidth,
                height: effectiveHeight,
                display: "flex",
                justifyContent: "center",
                marginLeft: disabled ? jobCardOffset : 0, // apply offset only when disabled (in timeline view)
                opacity: hover ? 1.0 : job?.completed || job?.noShow == true || job?.preboardAssistanceRequired != undefined && job?.preboardAssistanceRequired == false ? 0.4 : 1
            }}>
            {props.children}
        </div>
    );
});

export default JobCardContainer;